import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import { ProfileContext } from "@app/provider/profile";
import {
  BaseModalDialog,
  InfoCard,
  SpinnerView,
  UpdatePhoneForm,
  VerificationPhoneModal,
} from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { useDialog } from "@natera/platform/lib/hooks";
import { Button } from "@natera/form";
import { MfaErrorProvider, MfaSetupContext } from "@app/provider";
import {
  EnrollFactorResponse,
  Factor,
  FACTORS_TO_SHOW,
  FactorStatus,
} from "@app/service/user";
import VerifyModal from "@app/components/verifyModal";

const messages = defineMessages({
  updatePhoneBackButton: {
    id: "updatePhoneBackButton",
    defaultMessage: "Contact Details",
  },
  updatePhoneTitle: {
    id: "updatePhoneTitle",
    defaultMessage: "Mobile Phone",
  },
  updatePhoneDialogTitle: {
    id: "updatePhoneDialogTitle",
    defaultMessage:
      "Changing your mobile number will require you to verify your new mobile number",
  },
  updatePhoneDialogContent: {
    id: "updatePhoneDialogContent",
    defaultMessage: "Are you sure you want to proceed?",
  },
  updatePhoneDialogSubmit: {
    id: "updatePhoneDialogSubmit",
    defaultMessage: "Yes, Continue With Update",
  },
  updatePhoneDialogCancelButton: {
    id: "updatePhoneDialogCancelButton",
    defaultMessage: "Cancel",
  },
});

const UpdatePhone: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [factor, setFactor] = useState<Factor | EnrollFactorResponse>();
  const [showVerificationCodeModal, setShowVerificationCodeModal] =
    useState<boolean>(false);

  const {
    profileData: patientProfileData,
    profileDataIsLoading,
    updatePhone,
    updatePatientIsLoading,
  } = useContext(ProfileContext);

  const {
    isFactorActivated,
    setIsFactorActivated,
    isUpdatePhoneAction,
    setIsUpdatePhoneAction,
    loadEnrolledFactorList,
    isLoading: isFactorLoading,
  } = useContext(MfaSetupContext);

  const [phoneNumber, setPhoneNumber] = useState("");

  const updatePhoneDialog = useDialog(BaseModalDialog);

  useEffect(() => {
    const isPhoneUpdated =
      phoneNumber && isUpdatePhoneAction && isFactorActivated;
    if (isPhoneUpdated) {
      (async () => {
        await updatePhone(phoneNumber);
        setIsUpdatePhoneAction(false);
        setIsFactorActivated(false);
      })();
    }
  }, [isUpdatePhoneAction, isFactorActivated, setIsUpdatePhoneAction]);

  useEffect(() => {
    if (phoneNumber) {
      updatePhoneDialog.open(updatePhoneDialogProps());
    }
  }, [phoneNumber]);

  const updatePhoneDialogProps = () => {
    return {
      header: intl.formatMessage(messages.updatePhoneDialogTitle),
      content: <p>{intl.formatMessage(messages.updatePhoneDialogContent)}</p>,
      actions: (
        <>
          <Button onClick={handleDialogCancel} outlined>
            {intl.formatMessage(messages.updatePhoneDialogCancelButton)}
          </Button>
          <Button unelevated onClick={handleDialogSubmit}>
            {intl.formatMessage(messages.updatePhoneDialogSubmit)}
          </Button>
        </>
      ),
      handleClose: handleDialogCancel,
    };
  };

  const isActivePhoneFactor = (factor: Factor) =>
    factor.status === FactorStatus.ACTIVE &&
    (factor.factorType === FACTORS_TO_SHOW.sms ||
      factor.factorType === FACTORS_TO_SHOW.call);

  const handleDialogSubmit = async () => {
    const enrolledFactorList = await loadEnrolledFactorList();
    const activePhoneFactors = enrolledFactorList.filter((factor: Factor) =>
      isActivePhoneFactor(factor)
    );
    if (activePhoneFactors.length) {
      const activeFactor = {
        ...activePhoneFactors[0],
        profile: { phoneNumber },
      };
      setFactor(activeFactor);
    } else {
      setShowVerificationCodeModal(true);
    }
    updatePhoneDialog.close();
  };

  const handleDialogCancel = () => {
    setPhoneNumber("");
    updatePhoneDialog.close();
  };

  const handleSubmit = async (phone: string) => {
    if (phone === patientProfileData?.phone?.slice(1)) {
      navigate(routes.contactDetails, { replace: true });
    }
    setPhoneNumber(phone);
  };

  const handleCancel = () => navigate(routes.contactDetails);

  const renderView = () => {
    if (factor) {
      return <VerifyModal factor={factor} setFactor={setFactor} updatePhone />;
    } else if (showVerificationCodeModal) {
      return <VerificationPhoneModal phoneNumber={phoneNumber} />;
    } else {
      return (
        <BlueHeaderContent
          blueHeaderTitle={intl.formatMessage(messages.updatePhoneBackButton)}
          backArrowHeaderRoute={routes.contactDetails}
        >
          <InfoCard titleText={intl.formatMessage(messages.updatePhoneTitle)}>
            <UpdatePhoneForm
              currentPhone={patientProfileData?.phone}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
            />
          </InfoCard>
          <SpinnerView
            isLoading={
              profileDataIsLoading || updatePatientIsLoading || isFactorLoading
            }
          />
          {updatePhoneDialog.getDialog()}
        </BlueHeaderContent>
      );
    }
  };

  return <MfaErrorProvider>{renderView()}</MfaErrorProvider>;
};

export default UpdatePhone;
